import { FC, useCallback, useMemo, useState } from 'react';
import { Select, Collapse } from 'antd';
import { LocalStorage, useLocalStorage } from '@/core/Storage';
import { getAccoutKey, useUserData } from '@/shared/access';
import { useGettext } from '@cranium/i18n';
import { TzModal } from '@/shared/tz/Timezone/TzModal';
import styled from 'styled-components';
import { useSaveTimezoneRequest } from '@/shared/tz/Timezone/queries/saveTimezone.generated';
const PartnerTZ = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

export const Timezone: FC<{ mobile?: boolean }> = ({ mobile, ...props }) => {
  const { gettext } = useGettext();
  const meData = useUserData();
  const save = useSaveTimezoneRequest();
  const [open, setOpen] = useState<boolean>(false);
  const cachePartner = LocalStorage.getItem<{ partner: { timezone: string } }>(getAccoutKey('account'));
  const cachedTimeZone = LocalStorage.getItem<string>(getAccoutKey('tz'));
  const { value, setValue } = useLocalStorage<{ value: string; label: string }[]>(getAccoutKey('user-timezones'));
  const options = useMemo(() => {
    const addNew = {
      label: <b>{gettext('Add new timezone')}</b>,
      value: 'select_timezone'
    };
    const partnerTZ = {
      label: (
        <PartnerTZ>
          <b>{gettext('Partner timezone:')}</b>
          {cachePartner?.partner?.timezone}
        </PartnerTZ>
      ),
      value: cachePartner?.partner?.timezone
    };
    if (!value && !cachePartner?.partner?.timezone) {
      return [addNew];
    }
    if (!value && cachePartner?.partner?.timezone) {
      return [partnerTZ, addNew];
    }
    if (Array.isArray(value)) {
      return [
        ...(cachePartner?.partner?.timezone ? [partnerTZ] : []),
        ...value.filter(({ value }) => value !== cachePartner?.partner?.timezone),
        addNew
      ];
    }
  }, [value]);

  const handleChange = useCallback(
    async (value?: string) => {
      if (value === 'select_timezone') {
        return setOpen(true);
      }
      const id = meData?.partnerMember?.id;
      if (!value || !id) return;
      await save({ input: { workingTimezone: value, id } });
      LocalStorage.setItem(getAccoutKey('tz'), value);
      window.location.reload();
    },
    [meData?.partnerMember?.id]
  );
  if (mobile) {
    return (
      <>
        <Collapse.Panel
          {...props}
          key="profile"
          header={
            <Root>
              <i className="icon-FieldTime" />
              {cachedTimeZone || cachePartner?.partner?.timezone}
            </Root>
          }
        >
          {options?.map(option => (
            <Item key={option.value} onClick={() => handleChange(option.value)}>
              {option.label}
            </Item>
          ))}
        </Collapse.Panel>
        <TzModal
          open={open}
          setOpen={setOpen}
          onChange={tz => {
            setValue([{ label: tz, value: tz }, ...(value || [])?.filter(item => item.value !== tz)]);
            handleChange(tz);
          }}
        />
      </>
    );
  }
  return (
    <div style={{ width: '200px', display: 'flex', flexDirection: 'column' }}>
      <Select
        filterOption={(search, value) => {
          if (!search) return true;
          if (value?.value === 'select_timezone') return true;
          return !!value?.value?.toLowerCase().includes(search?.toLowerCase());
        }}
        optionLabelProp="value"
        options={options}
        showSearch
        value={cachedTimeZone || cachePartner?.partner?.timezone}
        onChange={handleChange}
      />
      <TzModal
        open={open}
        setOpen={setOpen}
        onChange={tz => {
          setValue([{ label: tz, value: tz }, ...(value || [])?.filter(item => item.value !== tz)]);
          handleChange(tz);
        }}
      />
    </div>
  );
};

const Item = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  font: var(--font-normal-16) !important;
  color: var(--gray-7) !important;
  display: flex;
  padding: 4px 8px;
  border-radius: 4px;
  width: 100%;
  min-height: 48px;
  align-items: center;
  padding-left: 40px;
  &:hover {
    background: var(--green-2) !important;
    color: var(--gray-9) !important;
  }
`;

const Root = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font: var(--font-normal-18) !important;
  color: var(--gray-7) !important;
  height: 48px;
  padding-left: 8px;
  &:hover {
    background: var(--green-2) !important;
    color: var(--gray-9) !important;
    border-radius: 4px !important;
  }
  .icon-FieldTime {
    font-size: 22px !important;
    color: var(--gray-7) !important;
  }
`;
